import React, { lazy, Component } from 'react';
import { Check, Star, ShieldCheck, ChevronDown } from 'lucide-react';
const BUY_LINK = 'https://impulsodigital.vip/cart/44852087357526:1';
const HERO_IMG = 'https://cdn.magicpatterns.com/uploads/o3iNKMVMPsNdrpnqkgBn4j/ChatGPT_Image_19_feb_2026_11_21_55_a.m._1-removebg-preview.webp';
// Reusable CTA Button Component to ensure consistency and single point of edit
const CTAButton = ({
  className = '',
  children = 'QUIERO LAS 365 ACTIVIDADES'
}: {
  className?: string;
  children?: React.ReactNode;
}) => <a href={BUY_LINK} className={`bg-primary text-white font-bold text-center rounded-xl py-4 px-6 block w-full shadow-cta hover:bg-primaryHover transition-colors active:scale-[0.98] ${className}`} data-id="element-0">
    {children}
  </a>;
export function App() {
  return <div className="w-full min-h-screen bg-white pb-20" data-id="element-1">
      {/* Main Wrapper: Limits width on desktop to maintain high-converting funnel layout */}
      <main className="max-w-xl mx-auto bg-white shadow-2xl min-h-screen relative" data-id="element-2">
        {/* ==========================================
            SECCIÓN 1 - HERO
            ========================================== */}
        <section className="px-5 pt-6 pb-10 flex flex-col items-center text-center" data-id="element-3">
          <div className="bg-light text-dark text-[10px] sm:text-xs font-bold px-4 py-1.5 rounded-full mb-5 tracking-wide uppercase border border-gray-200" data-id="element-4">
            Para mamás de niños de 1 a 10 años
          </div>

          <h1 className="text-[28px] sm:text-3xl font-extrabold leading-[1.15] mb-4 text-dark tracking-tight" data-id="element-5">
            Tu hijo no necesita más pantallas.{' '}
            <span className="text-primary block mt-1" data-id="element-6">
              Necesita actividades que lo ayuden a crecer.
            </span>
          </h1>

          <p className="text-sm sm:text-base text-gray-600 mb-6 leading-relaxed px-2" data-id="element-7">
            Descubrí las 365 actividades que ya ayudaron a miles de familias a
            reducir el tiempo frente al celular, recuperar la tranquilidad y
            estimular el desarrollo real de sus hijos.
          </p>

          {/* Hero Image - Eager load for LCP optimization */}
          <div className="w-full max-w-[260px] mx-auto mb-6 relative" data-id="element-8">
            <div className="absolute inset-0 bg-primary/20 blur-2xl rounded-full transform scale-90 -z-10" data-id="element-9"></div>
            <img src={HERO_IMG} alt="Libro 365 Actividades Sin Pantallas" fetchPriority="high" loading="eager" width="260" height="340" className="w-full h-auto object-contain drop-shadow-2xl" data-id="element-10" />
          </div>

          <ul className="text-left text-sm font-semibold space-y-2.5 mb-8 w-full max-w-[280px]" data-id="element-11">
            {['Solo 15 minutos al día', 'Sin materiales complicados', 'Actividades listas para usar', 'Acceso inmediato'].map((text, i) => <li key={i} className="flex items-center gap-3" data-id="element-12">
                <div className="bg-primary/10 p-1 rounded-full" data-id="element-13">
                  <Check className="w-4 h-4 text-primary shrink-0 stroke-[3]" data-id="element-14" />
                </div>
                <span className="text-gray-800" data-id="element-15">{text}</span>
              </li>)}
          </ul>

          <div className="mb-5 w-full" data-id="element-16">
            <span className="text-gray-400 line-through text-sm block font-medium" data-id="element-17">
              Valor anterior: $49.990
            </span>
            <span className="text-4xl font-black text-dark tracking-tight" data-id="element-18">
              Hoy: $19.990
            </span>
          </div>

          <CTAButton data-id="element-19" />
        </section>

        {/* ==========================================
            SECCIÓN 2 - EL PROBLEMA
            ========================================== */}
        <section className="bg-light py-12 px-5 border-y border-gray-100" data-id="element-20">
          <h2 className="text-2xl sm:text-3xl font-bold mb-4 text-center tracking-tight" data-id="element-21">
            No es culpa tuya.
          </h2>
          <p className="text-center text-gray-600 mb-8 text-sm sm:text-base px-2" data-id="element-22">
            Cuando no tenemos alternativas listas, las pantallas terminan
            ocupando cada momento libre.
          </p>

          <div className="bg-white rounded-2xl p-6 shadow-card space-y-4 border border-gray-100" data-id="element-23">
            {[{
            emoji: '📱',
            text: 'Lo usará "solo 10 minutos".'
          }, {
            emoji: '📱',
            text: 'Termina siendo una hora.'
          }, {
            emoji: '📱',
            text: 'Aparecen berrinches.'
          }, {
            emoji: '📱',
            text: 'Se vuelve más difícil apagarlo.'
          }, {
            emoji: '📱',
            text: 'Vos terminás agotada.'
          }].map((item, i) => <div key={i} className="flex items-center gap-4" data-id="element-24">
                <span className="text-2xl" data-id="element-25">{item.emoji}</span>
                <p className="font-medium text-gray-800" data-id="element-26">{item.text}</p>
              </div>)}
          </div>
        </section>

        {/* ==========================================
            SECCIÓN 3 - LA CIENCIA
            ========================================== */}
        <section className="py-12 px-5" data-id="element-27">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center tracking-tight" data-id="element-28">
            Lo que dicen los expertos.
          </h2>
          <div className="grid gap-4 sm:grid-cols-2" data-id="element-29">
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 shadow-sm" data-id="element-30">
              <p className="font-black text-3xl text-primary mb-2" data-id="element-31">90%</p>
              <p className="text-sm text-gray-700 font-medium" data-id="element-32">
                del desarrollo cerebral ocurre en los primeros años.
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 shadow-sm" data-id="element-33">
              <p className="font-black text-3xl text-primary mb-2" data-id="element-34">73%</p>
              <p className="text-sm text-gray-700 font-medium" data-id="element-35">
                de los pediatras recomiendan reducir la exposición excesiva.
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 shadow-sm" data-id="element-36">
              <p className="font-black text-3xl text-primary mb-2" data-id="element-37">2x</p>
              <p className="text-sm text-gray-700 font-medium" data-id="element-38">
                más riesgo de retrasos en ciertas habilidades por exceso de
                pantallas.
              </p>
            </div>
            <div className="bg-gray-50 border border-gray-100 rounded-xl p-5 shadow-sm" data-id="element-39">
              <p className="font-black text-3xl text-primary mb-2" data-id="element-40">15 min</p>
              <p className="text-sm text-gray-700 font-medium" data-id="element-41">
                de actividades guiadas pueden generar cambios reales.
              </p>
            </div>
          </div>
        </section>

        {/* ==========================================
            SECCIÓN 4 - LA SOLUCIÓN
            ========================================== */}
        <section className="bg-light py-12 px-5 border-y border-gray-100" data-id="element-42">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center tracking-tight leading-tight" data-id="element-43">
            Una actividad por día.
            <br data-id="element-44" />
            Un cambio para toda la familia.
          </h2>
          <div className="grid grid-cols-2 gap-4" data-id="element-45">
            {[{
            emoji: '🧠',
            text: 'Desarrollo cognitivo'
          }, {
            emoji: '🎨',
            text: 'Creatividad'
          }, {
            emoji: '🗣️',
            text: 'Lenguaje'
          }, {
            emoji: '🤝',
            text: 'Autonomía'
          }, {
            emoji: '😌',
            text: 'Más tranquilidad para mamá'
          }, {
            emoji: '📚',
            text: 'Menos dependencia de pantallas'
          }].map((item, i) => <div key={i} className="bg-white p-4 rounded-xl shadow-card border border-gray-100 text-center flex flex-col items-center justify-center gap-2" data-id="element-46">
                <span className="text-3xl mb-1" data-id="element-47">{item.emoji}</span>
                <p className="text-xs sm:text-sm font-semibold text-gray-800 leading-tight" data-id="element-48">
                  {item.text}
                </p>
              </div>)}
          </div>
        </section>

        {/* ==========================================
            SECCIÓN 5 - QUÉ INCLUYE
            ========================================== */}
        <section className="py-10 px-5" data-id="element-49">
          <h2 className="text-2xl sm:text-3xl font-bold mb-2 text-center tracking-tight" data-id="element-50">
            Todo lo que recibís hoy.
          </h2>

          <h3 className="text-lg font-bold text-center mb-5 text-primary" data-id="element-51">
            365 Actividades Sin Pantallas
          </h3>

          <div className="bg-gray-50 rounded-2xl p-5 border border-gray-100 max-w-md mx-auto" data-id="element-52">
            <ul className="space-y-3" data-id="element-53">
              {['Juegos de motricidad.', 'Juegos sensoriales.', 'Actividades creativas.', 'Juegos para interiores.', 'Actividades rápidas.', 'Actividades educativas.', 'Ideas por edades.'].map((text, i) => <li key={i} className="flex items-start gap-3" data-id="element-54">
                  <div className="bg-primary/10 p-1 rounded-full mt-0.5" data-id="element-55">
                    <Check className="w-4 h-4 text-primary shrink-0 stroke-[3]" data-id="element-56" />
                  </div>
                  <span className="text-gray-800 font-medium" data-id="element-57">{text}</span>
                </li>)}
            </ul>
          </div>
        </section>

        {/* ==========================================
            SECCIÓN 6 - BONOS
            ========================================== */}
        <section className="bg-dark text-white py-12 px-5" data-id="element-58">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center tracking-tight leading-tight" data-id="element-59">
            Además recibís estos regalos{' '}
            <span className="text-primary" data-id="element-60">GRATIS</span>.
          </h2>

          <div className="space-y-4" data-id="element-61">
            {[{
            img: 'https://cdn.magicpatterns.com/uploads/579iUroFx2nfUGQt3D4DQm/ChatGPT_Image_17_feb_2026_10_25_14_p.m._1.webp',
            title: 'No me grites, mamá',
            desc: 'Guía práctica para dejar los gritos y conectar mejor con tu hijo.',
            value: '$11.990'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/iCAC2W31xWDi16643Hqcz3/Cuentos_Saludables_Comprimido.webp',
            title: 'Cuentos Saludables',
            desc: 'Cuentos ilustrados que ayudan a los chicos a comer mejor y crear buenos hábitos.',
            value: '$9.990'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/rY7TyiJd6Fn8VJX3dVQ7Xk/ChatGPT_Image_17_feb_2026_10_28_27_p.m.webp',
            title: '4 hábitos de autonomía',
            desc: 'Pasos simples para criar niños más seguros e independientes.',
            value: '$9.990'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/qWpVxGytrTAp3VagsRUJXH/WhatsApp_Image_2026-01-29_at_09.57.10.webp',
            title: 'Snacks Inteligentes',
            desc: 'Opciones saludables para el refrigerio y la vianda escolar.',
            value: '$9.990'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/wgSk36FhePs2GcQCXaKuUb/ChatGPT_Image_17_feb_2026_10_29_30_p.m.webp',
            title: 'Comidas Saludables para Cada Momento del Día',
            desc: 'Desayunos, almuerzos y cenas equilibradas para niños.',
            value: '$9.990'
          }, {
            img: 'https://cdn.magicpatterns.com/uploads/ppZroZpJdgaVDV6MLKU1VZ/ChatGPT_Image_28_dic_2025_08_32_37_p.m.webp',
            title: 'Primeros Auxilios para Niños',
            desc: 'Guía básica para actuar con calma ante pequeños accidentes.',
            value: '$12.990'
          }].map((bono, i) => <div key={i} className="bg-[#1A1A1A] rounded-xl p-3 border border-gray-800 flex gap-4 relative overflow-hidden" data-id="element-62">
                <div className="absolute top-0 right-0 bg-primary text-white text-[10px] font-bold px-2 py-1 rounded-bl-lg z-10" data-id="element-63">
                  GRATIS
                </div>

                <div className="w-24 sm:w-28 shrink-0 aspect-[3/4] rounded-lg overflow-hidden bg-white" data-id="element-64">
                  <img src={bono.img} alt={`Bono: ${bono.title}`} loading="lazy" className="w-full h-full object-cover" data-id="element-65" />
                </div>

                <div className="flex flex-col flex-1 min-w-0 pr-12" data-id="element-66">
                  <h3 className="font-bold text-base mb-1 leading-tight text-white" data-id="element-67">
                    {bono.title}
                  </h3>
                  <p className="text-xs text-gray-400 mb-3 flex-grow leading-relaxed" data-id="element-68">
                    {bono.desc}
                  </p>

                  <div className="mt-auto flex items-baseline gap-2" data-id="element-69">
                    <span className="text-xs text-gray-500 line-through font-medium" data-id="element-70">
                      {bono.value}
                    </span>
                    <span className="text-sm font-bold text-primary" data-id="element-71">
                      HOY: GRATIS
                    </span>
                  </div>
                </div>
              </div>)}
          </div>

          <div className="mt-8 text-center bg-primary/10 border border-primary/20 rounded-xl p-4" data-id="element-72">
            <p className="font-bold text-primary" data-id="element-73">
              Valor total percibido superior a $50.000.
            </p>
          </div>
        </section>

        {/* ==========================================
            SECCIÓN 7 - TESTIMONIOS
            ========================================== */}
        <section className="py-12 px-5 bg-light" data-id="element-74">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center tracking-tight" data-id="element-75">
            Miles de familias ya comenzaron.
          </h2>

          <div className="space-y-4" data-id="element-76">
            {[{
            name: 'María L.',
            city: 'Buenos Aires',
            text: 'Desde que empezamos con las actividades, las tardes son mucho más tranquilas. Mi hijo de 4 años ya no pide el celular a los gritos.'
          }, {
            name: 'Sofía R.',
            city: 'Córdoba',
            text: 'Me salvó la vida. Trabajo desde casa y me sentía re culpable por darle la tablet. Ahora tenemos rutinas de juego hermosas.'
          }, {
            name: 'Laura M.',
            city: 'Rosario',
            text: 'Las ideas son súper simples y no necesito comprar materiales raros. Con lo que hay en casa nos divertimos horas.'
          }, {
            name: 'Carolina T.',
            city: 'Mendoza',
            text: 'Vale cada centavo. El cambio en el humor de mi nene de 6 años fue increíble. ¡Gracias!'
          }].map((testimonial, i) => <div key={i} className="bg-white p-5 rounded-xl shadow-card border border-gray-100" data-id="element-77">
                <div className="flex text-[#FFB800] mb-3" data-id="element-78">
                  {[...Array(5)].map((_, j) => <Star key={j} className="w-4 h-4 fill-current" data-id="element-79" />)}
                </div>
                <p className="text-gray-700 text-sm italic mb-4 leading-relaxed" data-id="element-80">
                  "{testimonial.text}"
                </p>
                <div className="flex items-center justify-between" data-id="element-81">
                  <div data-id="element-82">
                    <p className="font-bold text-sm text-dark" data-id="element-83">
                      {testimonial.name}
                    </p>
                    <p className="text-xs text-gray-500" data-id="element-84">{testimonial.city}</p>
                  </div>
                  <div className="flex items-center gap-1 text-primary bg-primary/10 px-2 py-1 rounded text-[10px] font-bold" data-id="element-85">
                    <ShieldCheck className="w-3 h-3" data-id="element-86" />
                    Compra verificada
                  </div>
                </div>
              </div>)}
          </div>
        </section>

        {/* ==========================================
            SECCIÓN 8 - GARANTÍA
            ========================================== */}
        <section className="py-12 px-5" data-id="element-87">
          <h2 className="text-2xl sm:text-3xl font-bold mb-6 text-center tracking-tight" data-id="element-88">
            Probalo sin riesgo.
          </h2>

          <div className="bg-primary/5 border-2 border-primary rounded-2xl p-6 text-center relative overflow-hidden" data-id="element-89">
            <ShieldCheck className="w-16 h-16 text-primary mx-auto mb-4 opacity-20 absolute top-4 right-4" data-id="element-90" />
            <ShieldCheck className="w-12 h-12 text-primary mx-auto mb-4 relative z-10" data-id="element-91" />
            <p className="text-gray-800 font-medium leading-relaxed relative z-10" data-id="element-92">
              Tenés <strong className="text-dark" data-id="element-93">7 días de garantía</strong>.
              Si sentís que esta guía no te ayuda, te devolvemos tu dinero.
            </p>
          </div>
        </section>

        {/* ==========================================
            SECCIÓN 9 - OFERTA FINAL
            ========================================== */}
        <section className="bg-dark text-white py-12 px-5 text-center" data-id="element-94">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 tracking-tight leading-tight" data-id="element-95">
            Todo lo que necesitás para empezar hoy.
          </h2>

          <div className="bg-[#1A1A1A] rounded-2xl p-6 border border-gray-800 mb-8 max-w-sm mx-auto" data-id="element-96">
            <ul className="text-left space-y-4 mb-6 font-medium" data-id="element-97">
              <li className="flex items-center gap-3" data-id="element-98">
                <Check className="w-5 h-5 text-primary shrink-0" data-id="element-99" />
                <span data-id="element-100">365 Actividades Sin Pantallas</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-101">
                <Check className="w-5 h-5 text-primary shrink-0" data-id="element-102" />
                <span data-id="element-103">+ 6 Bonos Exclusivos</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-104">
                <Check className="w-5 h-5 text-primary shrink-0" data-id="element-105" />
                <span data-id="element-106">Acceso inmediato</span>
              </li>
              <li className="flex items-center gap-3" data-id="element-107">
                <Check className="w-5 h-5 text-primary shrink-0" data-id="element-108" />
                <span data-id="element-109">Garantía de 7 días</span>
              </li>
            </ul>

            <div className="border-t border-gray-800 pt-6" data-id="element-110">
              <span className="text-gray-400 line-through text-sm block mb-1" data-id="element-111">
                Valor total: $49.990
              </span>
              <span className="text-4xl font-black text-primary block mb-2" data-id="element-112">
                Precio final: $19.990
              </span>
            </div>
          </div>

          <CTAButton className="text-lg py-5 shadow-[0_0_30px_rgba(22,196,91,0.3)]" data-id="element-113" />
        </section>

        {/* ==========================================
            SECCIÓN 10 - FAQ
            ========================================== */}
        <section className="py-12 px-5 bg-light pb-32 md:pb-12" data-id="element-114">
          <h2 className="text-2xl sm:text-3xl font-bold mb-8 text-center tracking-tight" data-id="element-115">
            Preguntas Frecuentes
          </h2>

          <div className="space-y-3" data-id="element-116">
            {[{
            q: '¿Es digital?',
            a: 'Sí, es un ebook digital en formato PDF. Podés descargarlo o leerlo directamente desde cualquier dispositivo.'
          }, {
            q: '¿Para qué edades sirve?',
            a: 'Las actividades están pensadas y adaptadas para niños de 1 a 10 años, con variaciones según la etapa de desarrollo.'
          }, {
            q: '¿Necesito materiales especiales?',
            a: 'No. El 95% de las actividades se realizan con cosas que ya tenés en tu casa (papel, colores, cajas, etc).'
          }, {
            q: '¿Lo recibo al instante?',
            a: 'Sí, apenas se confirma el pago recibís un correo con el acceso inmediato a todo el material y los bonos.'
          }, {
            q: '¿Funciona si mi hijo ya usa muchas pantallas?',
            a: 'Totalmente. El enfoque es progresivo, ofreciendo alternativas tan divertidas que ellos mismos elegirán jugar en lugar de pedir el celular.'
          }, {
            q: '¿Puedo verlo desde el celular?',
            a: 'Sí, el formato está 100% optimizado para que lo leas cómodamente desde tu celular en cualquier momento.'
          }].map((faq, i) => <details key={i} className="bg-white rounded-xl border border-gray-200 group overflow-hidden" data-id="element-117">
                <summary className="font-semibold p-4 cursor-pointer flex justify-between items-center text-gray-800 select-none" data-id="element-118">
                  {faq.q}
                  <ChevronDown className="w-5 h-5 text-gray-400 group-open:rotate-180 transition-transform duration-200" data-id="element-119" />
                </summary>
                <div className="px-4 pb-4 text-gray-600 text-sm leading-relaxed border-t border-gray-50 pt-3" data-id="element-120">
                  {faq.a}
                </div>
              </details>)}
          </div>
        </section>
      </main>

      {/* ==========================================
                STICKY CHECKOUT CTA
                ========================================== */}
      <div className="fixed bottom-0 left-0 right-0 z-50 bg-white/95 backdrop-blur border-t border-gray-200" data-id="element-121">
        <div className="max-w-xl mx-auto flex items-center justify-between gap-3 px-4 py-3 pb-safe" data-id="element-122">
          <div className="flex flex-col gap-0.5" data-id="element-123">
            <div className="flex items-baseline gap-2" data-id="element-124">
              <span className="text-xl font-black leading-none text-dark" data-id="element-125">
                $19.990
              </span>
              <span className="text-xs text-gray-400 line-through font-medium" data-id="element-126">
                $49.990
              </span>
            </div>
            <span className="text-[11px] text-gray-500 leading-none" data-id="element-127">
              + 6 bonos y beneficios
            </span>
          </div>
          <a href={BUY_LINK} className="bg-primary text-white font-semibold text-sm rounded-lg py-2.5 px-5 whitespace-nowrap hover:bg-primaryHover transition-colors active:scale-[0.98]" data-id="element-128">
            Comprar ahora
          </a>
        </div>
      </div>
    </div>;
}